const ORDERING_SIGNAL_FIELDS = [
  'authority_ts',
  'authorityTs',
  'authorityTimestamp',
  'state_authority_ts',
  'match_authority_ts',
  'market_authority_ts',
  'market_state_authority_ts',
  'price_authority_ts',
  'odd_authority_ts',
  'betting_lock_authority_ts',
];

const VERSION_FIELDS = [
  'sequence',
  'state_version',
  'market_state_version',
  'price_version',
  'version',
];

const toIsoOrNull = (value) => {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const toFiniteNumberOrNull = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const firstPresentField = (row, fields) => {
  if (!row || typeof row !== 'object') return null;
  for (const field of fields) {
    const value = row[field];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const getEntityMap = (store, level) => {
  if (level === 'match') return store.latestMatchOrdering;
  if (level === 'market') return store.latestMarketOrdering;
  if (level === 'lineGroup') return store.latestLineGroupOrdering;
  return store.latestOddOrdering;
};

const normalizeId = (value) => {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : String(value);
};

const getMarketId = (market = {}) => normalizeId(market.id ?? market.market_id ?? market.marketId);
const getSelectionId = (selection = {}) => normalizeId(selection.id ?? selection.odds_id ?? selection.oddsId);
const getMatchId = (match = {}) => (
  match ? normalizeId(match.id ?? match.match_id ?? match.matchId) : null
);

const normalizeSocketBettingCategory = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  return normalized || null;
};

export const buildInplaySocketSubscriptionKey = (subscription, fallbackBettingType = null) => {
  const payload = subscription && typeof subscription === 'object'
    ? subscription
    : { id: subscription };
  const matchId = normalizeId(payload.id ?? payload.match_id ?? payload.matchId);
  if (!matchId) return null;

  const category = normalizeSocketBettingCategory(
    payload.betting_category
    ?? payload.category
    ?? (String(fallbackBettingType || '').toUpperCase() === 'LIVE' ? 'LIVE' : null)
  );

  return category ? `match:${category}:${matchId}` : `match:${matchId}`;
};

export const buildInplaySocketSubscriptionDiff = ({
  currentRooms = [],
  desiredSubscriptions = [],
  bettingType = null,
  force = false,
} = {}) => {
  const currentSet = force ? new Set() : new Set(Array.from(currentRooms || []).filter(Boolean));
  const desiredByRoom = new Map();

  (desiredSubscriptions || []).forEach((subscription) => {
    const room = buildInplaySocketSubscriptionKey(subscription, bettingType);
    if (!room || desiredByRoom.has(room)) return;
    desiredByRoom.set(room, subscription && typeof subscription === 'object'
      ? subscription
      : { id: subscription });
  });

  const desiredRooms = [...desiredByRoom.keys()].sort();
  const subscribe = desiredRooms
    .filter((room) => !currentSet.has(room))
    .map((room) => desiredByRoom.get(room));
  const unsubscribe = [...currentSet]
    .filter((room) => !desiredByRoom.has(room))
    .sort();

  return {
    desiredRooms,
    subscribe,
    unsubscribe,
    key: [String(bettingType || ''), ...desiredRooms].join('|'),
  };
};

const getMarketSelectionsKey = (market = {}) => (
  Array.isArray(market.selections) && !Array.isArray(market.odds) ? 'selections' : 'odds'
);

const getMarketSelections = (market = {}) => (
  Array.isArray(market.odds) ? market.odds : Array.isArray(market.selections) ? market.selections : []
);

const getLineGroupSelectionsKey = (lineGroup = {}) => (
  Array.isArray(lineGroup.selections) && !Array.isArray(lineGroup.odds) ? 'selections' : 'odds'
);

const hasLineGroupSelections = (lineGroup = {}) => (
  Array.isArray(lineGroup.odds) || Array.isArray(lineGroup.selections)
);

const getLineGroupSelections = (lineGroup = {}) => (
  Array.isArray(lineGroup.odds)
    ? lineGroup.odds
    : Array.isArray(lineGroup.selections)
      ? lineGroup.selections
      : []
);

const firstValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const normalized = String(value).trim();
    if (normalized) return value;
  }
  return null;
};

const SELECTION_LABEL_FIELDS = [
  'selectionDisplayName',
  'selection_display_label',
  'selection_name_display',
  'selection_name_display_ko',
  'name_ko',
  'name',
  'displayLabel',
  'selectionLabel',
];

const isPlaceholderSelectionLabel = (value) => {
  const normalized = String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  return normalized === '-'
    || normalized === '–'
    || normalized === '—'
    || normalized === '선택지 정보 대기'
    || normalized === 'selection info pending';
};

const cleanSelectionLabelValue = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim().replace(/\s+/g, ' ');
  if (!normalized || isPlaceholderSelectionLabel(normalized)) return null;
  return normalized;
};

const firstMeaningfulSelectionLabel = (selection = {}) => {
  for (const field of SELECTION_LABEL_FIELDS) {
    const label = cleanSelectionLabelValue(selection?.[field]);
    if (label) return label;
  }
  return null;
};

const mergeSelectionDisplayLabels = (currentSelection = {}, incomingSelection = {}, mergedSelection = {}) => {
  const incomingLabel = firstMeaningfulSelectionLabel(incomingSelection);
  const currentLabel = firstMeaningfulSelectionLabel(currentSelection);
  const bestLabel = incomingLabel || currentLabel;
  const next = { ...mergedSelection };

  for (const field of SELECTION_LABEL_FIELDS) {
    if (isPlaceholderSelectionLabel(next[field])) {
      delete next[field];
    }
  }

  if (!bestLabel) return next;

  for (const field of ['selectionDisplayName', 'selection_display_label', 'name_ko', 'name']) {
    if (!cleanSelectionLabelValue(next[field])) {
      next[field] = bestLabel;
    }
  }

  return next;
};

const buildV2SelectionIdentityKey = (source = {}, fallbackMarket = {}) => {
  const v2 = source?.v2_identity || {};
  const fields = source?.v2_contract_fields || {};
  const marketV2 = fallbackMarket?.v2_identity || {};
  const fixtureId = firstValue(v2.fixtureId, fields.fixtureId, marketV2.fixtureId);
  const marketId = firstValue(v2.marketId, fields.marketId, marketV2.marketId);
  const productMarketId = firstValue(v2.productMarketId, fields.productMarketId, marketV2.productMarketId, '__compat__');
  const lineId = firstValue(v2.lineId, fields.lineId);
  const selectionId = firstValue(v2.selectionId, fields.selectionId);
  if (!fixtureId || !marketId || !lineId || !selectionId) return null;
  return [fixtureId, marketId, productMarketId || '__compat__', lineId, selectionId]
    .map((part) => String(part).trim())
    .join('|');
};

const getSelectionMergeKey = (selection = {}, market = {}) => {
  const direct = firstValue(
    selection.identityKey,
    selection.identity_key,
    selection.v2_identity_key,
    selection.flashIdentityKey,
    selection.flash_identity_key,
    selection.v2_display_model?.flashIdentityKey,
    selection.v2_identity?.flashIdentityKey,
    selection.v2_identity?.identityKey,
  );
  if (direct !== null) return String(direct);
  const built = buildV2SelectionIdentityKey(selection, market);
  if (built) return built;
  return getSelectionId(selection);
};

const getLineGroupMergeKey = (lineGroup = {}, market = {}) => {
  const direct = firstValue(
    lineGroup.key,
    lineGroup.lineGroupKey,
    lineGroup.line_group_key,
    lineGroup.v2_display_model?.lineGroupKey,
  );
  if (direct !== null) return String(direct);
  const marketIdentity = market?.v2_identity || {};
  const lineSetId = firstValue(
    lineGroup.lineSetId,
    lineGroup.line_set_id,
    lineGroup.v2_display_model?.lineSetId,
    '__default__',
  );
  const marketKey = firstValue(market.customer_market_key, market.market_key, market.marketKey, market.v2_display_model?.marketKey, getMarketId(market));
  return [
    firstValue(marketIdentity.fixtureId, market.fixture_id, '__fixture__'),
    firstValue(marketIdentity.marketId, market.contract_market_id, marketKey, '__market__'),
    firstValue(marketIdentity.productMarketId, '__compat__'),
    lineSetId,
  ].map((part) => String(part || '').trim()).join('|');
};

const getMarketMergeKey = (market = {}) => {
  const direct = firstValue(
    market.customer_market_key,
    market.customerMarketKey,
    market.market_key,
    market.marketKey,
    market.v2_display_model?.marketKey,
  );
  if (direct !== null) return String(direct);
  const v2 = market.v2_identity || {};
  const fixtureId = firstValue(v2.fixtureId, market.fixture_id);
  const marketId = firstValue(v2.marketId, market.contract_market_id, market.raw_market_id);
  const productMarketId = firstValue(v2.productMarketId, '__compat__');
  if (fixtureId && marketId) return [fixtureId, marketId, productMarketId].map((part) => String(part).trim()).join('|');
  return getMarketId(market);
};

const hasExplicitHiddenReadModel = (entity = {}) => {
  const readModel = entity.v2_read_model || {};
  const visibleFalse = readModel.visible === false || entity.visible === false || entity.is_visible === false;
  const closure = Boolean(
    entity.closureBucket
    || entity.closure_bucket
    || entity.closureReason
    || entity.closure_reason
    || readModel.closureBucket
    || readModel.closureReason
  );
  const removed = entity.removed === true || entity.is_removed === true;
  return removed || (visibleFalse && closure);
};

const normalizeChangedKeyValue = (value) => {
  if (value === null || value === undefined) return [];
  const normalized = String(value).trim();
  if (!normalized) return [];
  if (normalized.startsWith('v2:')) return [normalized, normalized.slice(3)];
  return [normalized];
};

export const buildInplayChangedKeySet = (values = []) => {
  const set = new Set();
  (Array.isArray(values) ? values : []).forEach((value) => {
    normalizeChangedKeyValue(value).forEach((key) => set.add(key));
  });
  return set;
};

const hasKey = (set, key) => {
  if (!set || !key) return false;
  return normalizeChangedKeyValue(key).some((candidate) => set.has(candidate));
};

const getChangedKeySets = (options = {}) => ({
  identity: buildInplayChangedKeySet(options.changedIdentityKeys || options.changed_identity_keys || []),
  lineSet: buildInplayChangedKeySet(options.changedLineSetKeys || options.changed_line_set_keys || []),
  market: buildInplayChangedKeySet(options.changedMarketKeys || options.changed_market_keys || []),
});

const hasAnyChangedKey = (sets) => (
  sets.identity.size > 0 || sets.lineSet.size > 0 || sets.market.size > 0
);

const isDiffFriendlyProjectionMerge = (options = {}) => {
  if (options.preserveUnchangedOnFullProjection !== true) return false;
  return hasAnyChangedKey(getChangedKeySets(options));
};

const hasChangedSelection = (selection = {}, market = {}, options = {}) => {
  const key = getSelectionMergeKey(selection, market);
  return hasKey(getChangedKeySets(options).identity, key);
};

const hasChangedLineGroup = (lineGroup = {}, market = {}, options = {}) => {
  const sets = getChangedKeySets(options);
  if (hasKey(sets.lineSet, getLineGroupMergeKey(lineGroup, market))) return true;
  return getLineGroupSelections(lineGroup).some((selection) => hasKey(sets.identity, getSelectionMergeKey(selection, market)));
};

const hasChangedMarket = (market = {}, options = {}) => {
  const sets = getChangedKeySets(options);
  if (hasKey(sets.market, getMarketMergeKey(market))) return true;
  if (getMarketSelections(market).some((selection) => hasKey(sets.identity, getSelectionMergeKey(selection, market)))) {
    return true;
  }
  return (market.lineGroups || []).some((lineGroup) => hasChangedLineGroup(lineGroup, market, options));
};

export const createRealtimeOrderingGuard = () => ({
  latestMatchOrdering: new Map(),
  latestMarketOrdering: new Map(),
  latestLineGroupOrdering: new Map(),
  latestOddOrdering: new Map(),
});

export const normalizeRealtimeOrderingInfo = (row = {}, options = {}) => {
  const orderingValue = options.orderingTs ?? options.authorityTs ?? firstPresentField(row, ORDERING_SIGNAL_FIELDS);
  const orderingTs = toIsoOrNull(orderingValue);
  const sequence = toFiniteNumberOrNull(options.sequence ?? firstPresentField(row, VERSION_FIELDS));
  return {
    authority_ts: orderingTs,
    sequence,
    hasOrderingSignal: Boolean(orderingTs || sequence !== null),
  };
};

export const compareRealtimeOrderingInfo = (incoming = {}, current = {}) => {
  const incomingInfo = incoming.hasOrderingSignal === undefined ? normalizeRealtimeOrderingInfo(incoming) : incoming;
  const currentInfo = current.hasOrderingSignal === undefined ? normalizeRealtimeOrderingInfo(current) : current;

  if (!currentInfo.hasOrderingSignal) return incomingInfo.hasOrderingSignal ? 1 : 0;
  if (!incomingInfo.hasOrderingSignal) return -1;

  const incomingTime = incomingInfo.authority_ts ? Date.parse(incomingInfo.authority_ts) : NaN;
  const currentTime = currentInfo.authority_ts ? Date.parse(currentInfo.authority_ts) : NaN;
  if (Number.isFinite(incomingTime) && Number.isFinite(currentTime)) {
    if (incomingTime !== currentTime) return incomingTime > currentTime ? 1 : -1;
  } else if (Number.isFinite(incomingTime) !== Number.isFinite(currentTime)) {
    return Number.isFinite(incomingTime) ? 1 : -1;
  }

  if (incomingInfo.sequence !== null && currentInfo.sequence !== null) {
    if (incomingInfo.sequence !== currentInfo.sequence) return incomingInfo.sequence > currentInfo.sequence ? 1 : -1;
  } else if (incomingInfo.sequence !== null && currentInfo.sequence === null) {
    return 1;
  }

  return 0;
};

export const shouldApplyRealtimeOrderingGuard = (store, level, id, incoming, options = {}) => {
  const normalizedId = normalizeId(id);
  if (!normalizedId) return true;

  const entityMap = getEntityMap(store, level);
  const currentInfo = entityMap.get(normalizedId);
  const incomingInfo = normalizeRealtimeOrderingInfo(incoming, options);
  if (!currentInfo) return true;

  return compareRealtimeOrderingInfo(incomingInfo, currentInfo) >= 0;
};

export const commitRealtimeOrderingGuard = (store, level, id, incoming, options = {}) => {
  const normalizedId = normalizeId(id);
  if (!normalizedId) return null;

  const incomingInfo = normalizeRealtimeOrderingInfo(incoming, options);
  if (!incomingInfo.hasOrderingSignal) return null;
  getEntityMap(store, level).set(normalizedId, incomingInfo);
  return incomingInfo;
};

const commitExplicitHiddenOrderingGuard = (store, level, id, incoming, options = {}) => {
  if (!id) return true;
  if (!shouldApplyRealtimeOrderingGuard(store, level, id, incoming, options)) return false;
  commitRealtimeOrderingGuard(store, level, id, incoming, options);
  return true;
};

export const buildMarketSuspendOddDisplayGuardPayload = (oddsId, marketPayload = {}) => {
  const lockOrderingTs = (
    marketPayload.betting_lock_authority_ts
    || marketPayload.market_state_authority_ts
    || marketPayload.market_authority_ts
    || marketPayload.authority_ts
  );
  const lockSequence = (
    marketPayload.sequence
    ?? marketPayload.market_state_version
    ?? marketPayload.version
  );
  return {
    id: oddsId,
    odds_id: oddsId,
    is_suspended: marketPayload.is_suspended,
    ...(lockOrderingTs ? { authority_ts: lockOrderingTs } : {}),
    ...(lockSequence !== undefined && lockSequence !== null ? { sequence: lockSequence } : {}),
  };
};

export const commitMarketSuspendOddDisplayGuard = (store, oddsId, marketPayload = {}) => {
  const oddDisplayGuardPayload = buildMarketSuspendOddDisplayGuardPayload(oddsId, marketPayload);
  if (!shouldApplyRealtimeOrderingGuard(store, 'odd', oddsId, oddDisplayGuardPayload)) return false;
  commitRealtimeOrderingGuard(store, 'odd', oddsId, oddDisplayGuardPayload);
  return true;
};

export const mergeInplaySelectionWithDisplayGuard = (currentSelection, incomingSelection, store, options = {}) => {
  if (!incomingSelection) return currentSelection || incomingSelection;
  const selectionId = getSelectionId(incomingSelection) || getSelectionId(currentSelection);
  if (!selectionId) {
    const merged = currentSelection ? { ...currentSelection, ...incomingSelection } : incomingSelection;
    return mergeSelectionDisplayLabels(currentSelection, incomingSelection, merged);
  }

  if (!shouldApplyRealtimeOrderingGuard(store, 'odd', selectionId, incomingSelection, options)) {
    return currentSelection
      ? mergeSelectionDisplayLabels(currentSelection, incomingSelection, currentSelection)
      : null;
  }

  commitRealtimeOrderingGuard(store, 'odd', selectionId, incomingSelection, options);
  const merged = currentSelection ? { ...currentSelection, ...incomingSelection } : incomingSelection;
  return mergeSelectionDisplayLabels(currentSelection, incomingSelection, merged);
};

const mergeSelectionArray = (currentSelections = [], incomingSelections = [], store, options = {}, market = {}) => {
  const currentByKey = new Map();
  currentSelections.forEach((selection) => {
    const key = getSelectionMergeKey(selection, market);
    if (key) currentByKey.set(key, selection);
  });

  const seenKeys = new Set();
  const removedKeys = new Set();
  const mergedSelections = [];

  incomingSelections.forEach((selection) => {
    const key = getSelectionMergeKey(selection, market);
    const currentSelection = key ? currentByKey.get(key) : undefined;
    if (key) seenKeys.add(key);
    if (hasExplicitHiddenReadModel(selection)) {
      const selectionId = getSelectionId(selection) || getSelectionId(currentSelection) || key;
      if (!commitExplicitHiddenOrderingGuard(store, 'odd', selectionId, selection, options)) {
        if (currentSelection) mergedSelections.push(currentSelection);
        return;
      }
      if (key) removedKeys.add(key);
      return;
    }
    if (
      isDiffFriendlyProjectionMerge(options) &&
      key &&
      currentByKey.has(key) &&
      !hasChangedSelection(selection, market, options)
    ) {
      mergedSelections.push(currentByKey.get(key));
      return;
    }
    const mergedSelection = mergeInplaySelectionWithDisplayGuard(
      currentSelection,
      selection,
      store,
      options,
    );
    if (mergedSelection) mergedSelections.push(mergedSelection);
  });

  const replaceSelections = options.replaceSelections === true || options.replaceMarkets === true;
  if (!replaceSelections) {
    currentSelections.forEach((selection) => {
      const key = getSelectionMergeKey(selection, market);
      if (key && (seenKeys.has(key) || removedKeys.has(key))) return;
      mergedSelections.push(selection);
    });
  }

  return mergedSelections;
};

const withLineGroupSelections = (lineGroup = {}, selections = [], selectionKey = null) => {
  const key = selectionKey || getLineGroupSelectionsKey(lineGroup);
  if (key === 'selections') {
    const { odds: _odds, ...withoutOdds } = lineGroup;
    return { ...withoutOdds, selections };
  }
  const { selections: _selections, ...withoutSelections } = lineGroup;
  return { ...withoutSelections, odds: selections };
};

const mergeLineGroupWithDisplayGuard = (currentLineGroup, incomingLineGroup, store, options = {}, market = {}) => {
  if (!incomingLineGroup) return currentLineGroup || incomingLineGroup;
  if (hasExplicitHiddenReadModel(incomingLineGroup)) return null;
  if (
    isDiffFriendlyProjectionMerge(options) &&
    currentLineGroup &&
    !hasChangedLineGroup(incomingLineGroup, market, options)
  ) {
    return currentLineGroup;
  }

  const lineGroupId = getLineGroupMergeKey(incomingLineGroup || currentLineGroup, market);
  const canApplyLineGroup = lineGroupId
    ? shouldApplyRealtimeOrderingGuard(store, 'lineGroup', lineGroupId, incomingLineGroup, options)
    : true;
  if (!canApplyLineGroup && !currentLineGroup) return null;
  const baseLineGroup = canApplyLineGroup
    ? (currentLineGroup ? { ...currentLineGroup, ...incomingLineGroup } : { ...incomingLineGroup })
    : { ...(currentLineGroup || incomingLineGroup) };

  if (canApplyLineGroup && lineGroupId) {
    commitRealtimeOrderingGuard(store, 'lineGroup', lineGroupId, incomingLineGroup, options);
  }

  if (!hasLineGroupSelections(incomingLineGroup)) return baseLineGroup;

  const replaceSelections = options.replaceSelections === true || options.replaceMarkets === true;
  const incomingSelections = getLineGroupSelections(incomingLineGroup);
  if (!replaceSelections && incomingSelections.length === 0) {
    return withLineGroupSelections(
      baseLineGroup,
      getLineGroupSelections(currentLineGroup),
      getLineGroupSelectionsKey(currentLineGroup || incomingLineGroup),
    );
  }

  return withLineGroupSelections(
    baseLineGroup,
    mergeSelectionArray(
      getLineGroupSelections(currentLineGroup),
      incomingSelections,
      store,
      options,
      market,
    ),
    getLineGroupSelectionsKey(incomingLineGroup),
  );
};

const mergeLineGroupArray = (currentLineGroups = [], incomingLineGroups = [], store, options = {}, market = {}) => {
  const currentByKey = new Map();
  currentLineGroups.forEach((lineGroup) => {
    const key = getLineGroupMergeKey(lineGroup, market);
    if (key) currentByKey.set(key, lineGroup);
  });

  const seenKeys = new Set();
  const removedKeys = new Set();
  const merged = [];
  incomingLineGroups.forEach((lineGroup) => {
    const key = getLineGroupMergeKey(lineGroup, market);
    const currentLineGroup = key ? currentByKey.get(key) : undefined;
    if (key) seenKeys.add(key);
    if (hasExplicitHiddenReadModel(lineGroup)) {
      if (!commitExplicitHiddenOrderingGuard(store, 'lineGroup', key, lineGroup, options)) {
        if (currentLineGroup) merged.push(currentLineGroup);
        return;
      }
      if (key) removedKeys.add(key);
      return;
    }
    const next = mergeLineGroupWithDisplayGuard(currentLineGroup, lineGroup, store, options, market);
    if (next) merged.push(next);
  });

  if (options.replaceSelections !== true && options.replaceMarkets !== true) {
    currentLineGroups.forEach((lineGroup) => {
      const key = getLineGroupMergeKey(lineGroup, market);
      if (key && (seenKeys.has(key) || removedKeys.has(key))) return;
      merged.push(lineGroup);
    });
  }

  return merged;
};

export const mergeInplayMarketWithDisplayGuard = (currentMarket, incomingMarket, store, options = {}) => {
  if (!incomingMarket) return currentMarket || incomingMarket;
  if (hasExplicitHiddenReadModel(incomingMarket)) return null;
  if (
    isDiffFriendlyProjectionMerge(options) &&
    currentMarket &&
    !hasChangedMarket(incomingMarket, options)
  ) {
    return currentMarket;
  }
  const marketId = getMarketId(incomingMarket) || getMarketId(currentMarket);
  const canApplyMarket = marketId
    ? shouldApplyRealtimeOrderingGuard(store, 'market', marketId, incomingMarket, options)
    : true;
  if (!canApplyMarket && !currentMarket) return null;
  const baseMarket = canApplyMarket
    ? (currentMarket ? { ...currentMarket, ...incomingMarket } : { ...incomingMarket })
    : { ...(currentMarket || incomingMarket) };

  if (canApplyMarket && marketId) {
    commitRealtimeOrderingGuard(store, 'market', marketId, incomingMarket, options);
  }

  if (Array.isArray(incomingMarket.lineGroups)) {
    baseMarket.lineGroups = mergeLineGroupArray(
      Array.isArray(currentMarket?.lineGroups) ? currentMarket.lineGroups : [],
      incomingMarket.lineGroups,
      store,
      options,
      baseMarket,
    );
    const flattened = baseMarket.lineGroups.flatMap((lineGroup) => getLineGroupSelections(lineGroup));
    const marketSelectionKey = getMarketSelectionsKey(incomingMarket);
    if (marketSelectionKey === 'selections') {
      const { odds: _odds, ...withoutOdds } = baseMarket;
      return { ...withoutOdds, selections: flattened };
    }
    const { selections: _selections, ...withoutSelections } = baseMarket;
    return { ...withoutSelections, odds: flattened };
  }

  const incomingSelections = getMarketSelections(incomingMarket);
  const currentSelections = getMarketSelections(currentMarket);
  if (!incomingSelections.length) return baseMarket;
  const mergedSelections = mergeSelectionArray(currentSelections, incomingSelections, store, options, baseMarket);

  const selectionKey = getMarketSelectionsKey(incomingMarket);
  if (selectionKey === 'selections') {
    const { odds: _odds, ...withoutOdds } = baseMarket;
    return { ...withoutOdds, selections: mergedSelections };
  }

  const { selections: _selections, ...withoutSelections } = baseMarket;
  return { ...withoutSelections, odds: mergedSelections };
};

const mergeMarketArray = (currentMarkets = [], incomingMarkets = [], store, options = {}) => {
  const currentById = new Map(currentMarkets.map((market) => [getMarketMergeKey(market), market]));
  const seenIds = new Set();
  const removedIds = new Set();
  const merged = incomingMarkets.map((market) => {
    const marketId = getMarketMergeKey(market);
    const currentMarket = currentById.get(marketId);
    if (marketId) seenIds.add(marketId);
    if (hasExplicitHiddenReadModel(market)) {
      const orderingId = getMarketId(market) || getMarketId(currentMarket) || marketId;
      if (!commitExplicitHiddenOrderingGuard(store, 'market', orderingId, market, options)) {
        return currentMarket || null;
      }
      if (marketId) removedIds.add(marketId);
      return null;
    }
    return mergeInplayMarketWithDisplayGuard(currentMarket, market, store, options);
  }).filter(Boolean);
  if (options.replaceMarkets !== true) {
    currentMarkets.forEach((market) => {
      const marketId = getMarketMergeKey(market);
      if (marketId && (seenIds.has(marketId) || removedIds.has(marketId))) return;
      if (marketId) merged.push(market);
    });
  }
  return merged;
};

export const mergeInplayMatchWithDisplayGuard = (currentMatch, incomingMatch, store, options = {}) => {
  if (!incomingMatch) return currentMatch || incomingMatch;
  const incomingMatchId = getMatchId(incomingMatch);
  const currentMatchId = getMatchId(currentMatch);
  const currentBase = incomingMatchId && currentMatchId && incomingMatchId !== currentMatchId
    ? null
    : currentMatch;
  const matchId = incomingMatchId || currentMatchId;
  const canApplyMatch = matchId
    ? shouldApplyRealtimeOrderingGuard(store, 'match', matchId, incomingMatch, options)
    : true;
  const baseMatch = canApplyMatch
    ? (currentBase ? { ...currentBase, ...incomingMatch } : { ...incomingMatch })
    : { ...(currentBase || incomingMatch) };

  if (canApplyMatch && matchId) {
    commitRealtimeOrderingGuard(store, 'match', matchId, incomingMatch, options);
  }

  if (Array.isArray(incomingMatch.markets)) {
    baseMatch.markets = mergeMarketArray(currentBase?.markets || [], incomingMatch.markets, store, options);
  }
  if (incomingMatch.main_market) {
    baseMatch.main_market = mergeInplayMarketWithDisplayGuard(currentBase?.main_market, incomingMatch.main_market, store, options);
  }
  if (incomingMatch.handicap_pair && currentBase?.handicap_pair) {
    baseMatch.handicap_pair = { ...currentBase.handicap_pair, ...incomingMatch.handicap_pair };
  }

  return baseMatch;
};

export const shouldRunInplaySilentRecovery = ({
  socketConnected = false,
  lastWsEventAt = 0,
  lastSocketConnectedAt = 0,
  lastRecoveryAt = 0,
  now = Date.now(),
  gapMs = 12000,
  reconnectWindowMs = 5000,
  minIntervalMs = 8000,
} = {}) => {
  if (now - Number(lastRecoveryAt || 0) < minIntervalMs) return false;
  if (!socketConnected) return true;
  if (lastSocketConnectedAt && now - Number(lastSocketConnectedAt) <= reconnectWindowMs) return true;
  if (!lastWsEventAt) return false;
  return now - Number(lastWsEventAt) >= gapMs;
};
